import React, { useMemo, useState, useCallback } from 'react';
import { formatDisplayName } from '../../utils/formatTime';

function firstName(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/)[0];
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dateKeyToDate(key) {
  return new Date(key + 'T12:00:00');
}

function toDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function startOfWeekMon(d) {
  const copy = new Date(d);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  return copy;
}

function endOfWeekSun(d) {
  const copy = new Date(d);
  const day = copy.getDay();
  const diff = day === 0 ? 0 : 7 - day;
  copy.setDate(copy.getDate() + diff);
  return copy;
}

function rangeLabel(days) {
  if (!days || days.length === 0) return '';
  const first = dateKeyToDate(days[0].date);
  const last = dateKeyToDate(days[days.length - 1].date);
  const opts = { month: 'short', day: 'numeric' };
  const sameMonth = first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear();
  if (sameMonth) {
    return `${first.toLocaleDateString(undefined, { month: 'long' })} ${first.getDate()} – ${last.getDate()}, ${first.getFullYear()}`;
  }
  return `${first.toLocaleDateString(undefined, opts)} – ${last.toLocaleDateString(undefined, { ...opts, year: 'numeric' })}`;
}

export default function DashboardAttendanceRoster({ days }) {
  const list = Array.isArray(days) ? days : [];
  const today = todayKey();
  const [showPresent, setShowPresent] = useState(true);

  const togglePresent = useCallback(() => setShowPresent((v) => !v), []);

  const dataDateSet = useMemo(() => {
    const s = new Set();
    list.forEach((d) => { if (d?.date) s.add(d.date); });
    return s;
  }, [list]);

  const byDate = useMemo(() => {
    const m = {};
    list.forEach((d) => { if (d?.date) m[d.date] = d; });
    return m;
  }, [list]);

  const weeks = useMemo(() => {
    if (list.length === 0) return [];
    const firstDate = dateKeyToDate(list[0].date);
    const lastDate = dateKeyToDate(list[list.length - 1].date);
    const weekStart = startOfWeekMon(firstDate);
    const weekEnd = endOfWeekSun(lastDate);
    const rows = [];
    const cursor = new Date(weekStart);
    while (cursor <= weekEnd) {
      const row = [];
      for (let i = 0; i < 7; i++) {
        row.push(toDateKey(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
      rows.push(row);
    }
    return rows;
  }, [list]);

  if (list.length === 0) {
    return <div className="ac"><div className="ac-empty">No data for this period.</div></div>;
  }

  return (
    <div className="ac">
      <div className="ac-head">
        <span className="ac-title">{rangeLabel(list)}</span>
        <button
          type="button"
          className={`ac-show-btn ${showPresent ? 'ac-show-btn--on' : ''}`}
          onClick={togglePresent}
        >
          <i className={`bi ${showPresent ? 'bi-eye-fill' : 'bi-eye-slash'}`} />
          {showPresent ? 'Hide' : 'Show'} present
        </button>
      </div>

      <div className="ac-dow">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="ac-dow-cell">{d}</div>
        ))}
      </div>

      <div className="ac-grid">
        {weeks.map((week) =>
          week.map((key) => {
            const data = byDate[key];
            const d = dateKeyToDate(key);
            const dayNum = d.getDate();
            const dow = d.getDay();
            const isToday = key === today;
            const isWeekend = dow === 0 || dow === 6;
            const inRange = dataDateSet.has(key);
            const isHoliday = data?.isPublicHoliday;
            const isFuture = !!data?.isFuture;
            const isWorking = !isWeekend && !isHoliday && data?.isWorkingDay;
            const presentNames = isWorking ? (data.presentNames || []) : [];
            const absentNames = isWorking ? (data.absentNames || []) : [];
            const presentCount = presentNames.length;
            const total = presentCount + absentNames.length;
            const allIn = isWorking && absentNames.length === 0 && presentCount > 0;

            let cellCls = 'ac-cell';
            if (isToday) cellCls += ' ac-cell--today';
            if (isWeekend) cellCls += ' ac-cell--wknd';
            if (isHoliday) cellCls += ' ac-cell--hol';
            if (!inRange && !isWeekend) cellCls += ' ac-cell--out';

            return (
              <div key={key} className={cellCls}>
                <div className="ac-cell-top">
                  <span className={`ac-num ${isToday ? 'ac-num--today' : ''}`}>{dayNum}</span>
                  {isWorking && total > 0 && (
                    <span className={`ac-ratio ${allIn ? 'ac-ratio--ok' : ''}`}>
                      {presentCount}/{total}
                    </span>
                  )}
                </div>
                {isHoliday && (
                  <span className="ac-hol">{(data.publicHolidayNames || [])[0] || 'Holiday'}</span>
                )}
                {absentNames.length > 0 && (
                  <div className="ac-names-group ac-names-group--absent">
                    {absentNames.map((name) => (
                      <span key={name} className="ac-name ac-name--absent">
                        {firstName(formatDisplayName(name))}
                      </span>
                    ))}
                  </div>
                )}
                {showPresent && presentNames.length > 0 && (
                  <div className="ac-names-group ac-names-group--present">
                    {presentNames.map((name) => (
                      <span key={name} className="ac-name ac-name--present">{firstName(formatDisplayName(name))}</span>
                    ))}
                  </div>
                )}
                {!showPresent && allIn && <span className="ac-allin">All in</span>}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
